import { Form } from "antd";
import { useEffect, useRef, useState } from "react";

import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules, OperationAndIdMapping, Operations } from "../../../../constants/DatabaseConstants/MasterConstant";
import { CustomNotification } from "../../../../util/CustomNotification";
import useValue from "../../../../hooks/useValue";
import * as masterServices from "../../../../services/masterServices";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { RoleMasterListInterface } from "../../../../shared/interface/MasterManagement/roleMaster.interface";
import type { ACLMasterRecord } from "../../../../shared/interface/MasterManagement/accessControl.interface";

export const useAccessControlMaster = () => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [accessControlList, setAccessControlList] = useState<ACLMasterRecord[]>([]);
  const [roleId, setRoleId] = useState<number | null>(null);

  /* CUSTOM HOOKS */
  const { loading: roleListLoading, masterList: roleMasterList }: RoleMasterListInterface = useGetOneMasterList(ModuleAndIdMapping[Modules.ROLE_MASTER]);

  const { value: saving, handleDisableValue: handleDisableSaving, handleEnableValue: handleEnableSaving } = useValue(false);
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(false);

  /* HANDLERS */

  const handleUserRole = () => {
    setAccessControlList([]);
    const formRoleId = form.getFieldValue("role_id");
    setRoleId(formRoleId);
  };

  const handleCheckboxChange = (type: Operations, record: ACLMasterRecord) => {
    setAccessControlList((prevList) => prevList.map((item) => (item.module_id === record.module_id ? { ...item, [`${type}_action`]: item[`${type}_action` as keyof ACLMasterRecord] ? 0 : 1 } : item)));
  };

  /* handle submit record */
  const handleSubmit = async () => {
    try {
      handleEnableSaving();

      const formattedList = accessControlList?.map((item) => {
        // Mapping operation names to corresponding IDs
        const operation_id_list = ([Operations.CREATE, Operations.READ, Operations.UPDATE, Operations.DELETE] as const) //
          ?.filter((operation) => item[`${operation}_action` as keyof ACLMasterRecord])
          ?.map((operation) => OperationAndIdMapping[operation]); // Convert to corresponding numbers

        return {
          role_id: roleId,
          module_id: item?.module_id,
          operation_id_list,
          is_active: operation_id_list.length > 0 // False if empty, True otherwise
        };
      });

      const formData = new FormData();

      const modelId = ModuleAndIdMapping[Modules.WEB_FE_ACCESS_CONTROL_MASTER];

      formData.append("modelId", modelId.toString());
      formData.append("role_module_operations", JSON.stringify(formattedList));

      await masterServices.addMasterAPI(formData);

      const roleName = roleMasterList?.find((item) => item?.id === roleId);
      CustomNotification.showSuccessMessage(`${roleName?.rolename} role access has been updated successfully.`);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableSaving();
    }
  };

  /* EFFECTS */

  useEffect(() => {
    const fetchData = async (roleId: number) => {
      try {
        if (!roleId) return;
        handleEnableLoading();

        const response = await masterServices.getAccessControlMasterAPI(ModuleAndIdMapping[Modules.WEB_FE_ACCESS_CONTROL_MASTER], roleId);
        const formatResponseResult = response?.["AllRecordsList"]?.map((item, index) => {
          return {
            ...item,
            sr_no: index + 1
          };
        });

        setAccessControlList(formatResponseResult);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableLoading();
      }
    };

    if (roleId) fetchData(+roleId);
  }, [roleId]);

  return { loading, roleListLoading, form, saving, handleUserRole, handleCheckboxChange, handleSubmit, roleMasterList, formRef, dataList: accessControlList };
};
